// The patch-in-place backing store for `/v1/streams`, split from the connection
// loop beside it (`accountStream.ts`): one file holds the rows, the other holds
// the socket, and neither needs the other to be testable.

import type { StreamPatch } from '@developerz/domain';
import { produce, type SetStoreFunction } from 'solid-js/store';

/** Rows held per topic, keyed by id — the patch-in-place backing store. */
export type StreamStore = Record<string, Record<string, unknown>>;

/** Current rows for one topic, as an array (stable enough for list rendering). */
export function listTopic(store: StreamStore, topic: string): unknown[] {
  return Object.values(store[topic] ?? {});
}

/**
 * Apply one frame: upsert the row under `(topic, key)`, or drop it. `produce`
 * keeps it O(1) — only the row that moved re-renders, never the whole list.
 */
export function applyPatch(setStore: SetStoreFunction<StreamStore>, patch: StreamPatch): void {
  if ('deleted' in patch) {
    setStore(
      produce((s) => {
        const rows = s[patch.topic];
        if (rows) delete rows[patch.key];
      }),
    );
    return;
  }
  setStore(
    produce((s) => {
      const rows = s[patch.topic] ?? {};
      rows[patch.key] = patch.row;
      s[patch.topic] = rows;
    }),
  );
}

/**
 * Mark each topic the server named in `ready.subscribed` as SERVED, with no rows
 * yet. From this frame on the connection's snapshot is the truth for that topic,
 * and an empty snapshot sends no patch at all (`diffTopic` over nothing emits
 * nothing), so without this entry an empty topic was indistinguishable from "no
 * stream yet" and a topic-reading view fell back to the REST backfill: after
 * every ~10 min recycle a decided approval came back with live buttons (plan
 * 101 A5; the reader today is `mergeFleetLive`, `fleet-live-frames.ts`). An entry already holding rows is kept — `ready` is the first
 * frame of a connection, after `resetStore`, so there are none in practice.
 */
export function seedTopics(
  setStore: SetStoreFunction<StreamStore>,
  topics: readonly string[],
): void {
  setStore(
    produce((s) => {
      for (const t of topics) s[t] ??= {};
    }),
  );
}

/**
 * Drop every row. Called for a new topic set/org AND on every (re)connect.
 *
 * The server's diff state is per-CONNECTION (`pumpAccountStreams` allocates its
 * `versions` map fresh, and only emits `{deleted:true}` for keys IT saw), and it
 * recycles a stream every ~10 min. So after a recycle it re-sends the whole
 * snapshot as upserts and can never delete a row this client is still holding
 * from the previous connection — the store became the union of every snapshot
 * ever seen and only grew (~48 recycles in an 8h tab, on every route). It was
 * also wrong: a session that ended while the stream was cycling stayed on the
 * board forever.
 */
export function resetStore(setStore: SetStoreFunction<StreamStore>): void {
  setStore(
    produce((s) => {
      for (const k of Object.keys(s)) delete s[k];
    }),
  );
}
